import { z } from 'zod';
import { clientCreatedAt, isoDate, optText, text, uuid, type SyncFields } from './entities/common';

const amount = (max: number) => z.number().int().min(0).max(max);
export const projectionScenarioMultipliers = z.object({
  yieldBp: z.number().int().min(5000).max(15000),
  priceBp: z.number().int().min(5000).max(15000),
  feedBp: z.number().int().min(5000).max(15000),
  spcAddX10: z.number().int().min(-20).max(30),
});
export const DEFAULT_SCENARIOS = {
  low: { yieldBp: 8500, priceBp: 9000, feedBp: 11000, spcAddX10: 10 },
  high: { yieldBp: 11000, priceBp: 10500, feedBp: 9500, spcAddX10: -5 },
};
const scenarios = z.object({ low: projectionScenarioMultipliers, high: projectionScenarioMultipliers });
export const ProjectionAssumptions = z.object({
  milkPriceSantim: amount(100_000).nullable(),
  feedPerCowDaySantim: amount(1_000_000).nullable(),
  feedPerLiterSantim: amount(100_000).nullable(),
  calfMilkMlPerDay: z.number().int().min(0).max(15_000).nullable(),
  calfMilkDays: z.number().int().min(0).max(180).nullable(),
  calfFeedPerDaySantim: amount(1_000_000).nullable(),
  youngFeedPerDaySantim: amount(1_000_000).nullable(),
  heiferFeedPerDaySantim: amount(1_000_000).nullable(),
  vetPerAnimalYearSantim: amount(10_000_000).nullable(),
  aiCostPerServiceSantim: amount(1_000_000).nullable(),
  servicesPerConceptionX10: z.number().int().min(10).max(50).nullable(),
  laborPerMonthSantim: amount(10_000_000).nullable(),
  calfValueFemaleSantim: amount(100_000_000).nullable(),
  calfValueMaleSantim: amount(100_000_000).nullable(),
  maturePeakMl: z.number().int().min(1000).max(50_000).nullable(),
  scenarios: scenarios.default(DEFAULT_SCENARIOS),
});
export type ProjectionAssumptionsData = z.output<typeof ProjectionAssumptions>;
export const ProjectionInput = z.object({
  kind: z.enum(['COW', 'HEIFER', 'CALF']), startDate: isoDate,
  horizonMonths: z.union([z.literal(12), z.literal(24), z.literal(36)]),
  birthDate: isoDate, birthDateEstimated: z.boolean().default(false),
  breed: z.string().trim().max(60).optional(), purchasePriceSantim: amount(1_000_000_000),
  endValueSantim: amount(1_000_000_000).optional(),
  parity: z.number().int().min(1).max(15).optional(), milking: z.boolean().optional(),
  litersNowMl: z.number().int().min(1000).max(60_000).optional(),
  dimNow: z.number().int().min(5).max(400).optional(),
  lastPeakMl: z.number().int().min(1000).max(60_000).optional(),
  serviceDate: isoDate.optional(), monthsPregnant: z.number().int().min(1).max(9).optional(),
  expectedPeakMl: z.number().int().min(1000).max(60_000).optional(),
  measuredByOwner: z.boolean().default(false),
}).superRefine((v, ctx) => {
  const issue = (field: string) => ctx.addIssue({ code: 'custom', path: [field], message: 'Invalid for this animal or purchase date' });
  if (v.birthDate > v.startDate) issue('birthDate');
  if (v.serviceDate && v.serviceDate > v.startDate) issue('serviceDate');
  if (v.serviceDate && v.monthsPregnant !== undefined) issue('monthsPregnant');
  if (v.kind === 'COW') {
    if (v.parity === undefined) issue('parity');
    if (v.milking === undefined) issue('milking');
    if (v.milking && v.litersNowMl === undefined) issue('litersNowMl');
    if (v.milking && v.dimNow === undefined) issue('dimNow');
    if (v.milking && v.lastPeakMl !== undefined) issue('lastPeakMl');
    if (v.milking === false && v.litersNowMl !== undefined) issue('litersNowMl');
    if (v.milking === false && v.dimNow !== undefined) issue('dimNow');
    if (v.expectedPeakMl !== undefined) issue('expectedPeakMl');
  } else {
    for (const field of ['parity', 'milking', 'litersNowMl', 'dimNow', 'lastPeakMl'] as const) if (v[field] !== undefined) issue(field);
    if (v.kind === 'CALF' && (v.serviceDate || v.monthsPregnant !== undefined)) issue('serviceDate');
  }
});
export type ProjectionInputData = z.output<typeof ProjectionInput>;

export const projectionValueSource = z.enum(['OWNER', 'FARM', 'BUYER_PRICE', 'SETTING']);
const sourceMetadata = {
  source: projectionValueSource, from: isoDate.optional(), to: isoDate.optional(),
  sample: z.number().nonnegative().optional(), sampleUnit: z.string().max(80).optional(),
};
type SourceMetadata = { source: z.infer<typeof projectionValueSource>; from?: string; to?: string; sample?: number; sampleUnit?: string };
export type ResolvedProjectionAssumptions = {
  [K in Exclude<keyof ProjectionAssumptionsData, 'scenarios'>]: SourceMetadata & { value: NonNullable<ProjectionAssumptionsData[K]> };
} & { scenarios: ProjectionAssumptionsData['scenarios'] };
const valueShapes = ProjectionAssumptions.omit({ scenarios: true }).shape;
/** Saved snapshots must be complete, retaining the source of every resolved value. */
export const resolvedProjectionAssumptionsSchema = z.object({
  ...Object.fromEntries(Object.entries(valueShapes).map(([key, schema]) => [key, z.object({ value: schema.unwrap(), ...sourceMetadata })])),
  scenarios,
}) as unknown as z.ZodType<ResolvedProjectionAssumptions>;

export const projectionAssumptionSetSchema = z.object({ data: ProjectionAssumptions, clientCreatedAt });
export const projectionScenarioSchema = z.object({
  name: text(80), input: ProjectionInput, assumptions: resolvedProjectionAssumptionsSchema,
  linkedAnimalId: uuid.nullish(), note: optText(500), clientCreatedAt,
});
export type ProjectionAssumptionSetRecord = z.output<typeof projectionAssumptionSetSchema> & SyncFields;
export type ProjectionScenarioRecord = z.output<typeof projectionScenarioSchema> & SyncFields;

/** Completed purchase-date-anchored months; `to` is exclusive. Differences are ratios, not percent points. */
export interface ProjectionActualMonth {
  month: number;
  from: string;
  to: string;
  projectedMl: number;
  actualMl: number;
  recordedDays: number;
  days: number;
  projectedCostSantim: number;
  actualCostSantim: number;
  milkDifference: number | null;
  costDifference: number | null;
}
export interface ProjectionActualReport {
  asOf: string;
  projectionId: string;
  animal: { id: string; tag: string; name?: string | null };
  months: ProjectionActualMonth[];
}

export type ProjectionAssumptionKey = Exclude<keyof ProjectionAssumptionsData, 'scenarios'>;
export interface ProjectionFarmFigure {
  value: number | null;
  source: 'FARM' | 'BUYER_PRICE' | 'SETTING' | 'NONE';
  from?: string;
  to?: string;
  sample: number;
  sampleUnit: string;
  reason?: 'FEED_RATES_REQUIRED' | 'INSUFFICIENT_DATA' | 'OWNER_INPUT_REQUIRED';
}
export interface ProjectionDefaultsResponse {
  asOf: string;
  values: Record<ProjectionAssumptionKey, ProjectionFarmFigure>;
  herdMaxPeakMl: ProjectionFarmFigure;
}

const farmFigureSchema = z.object({
  value: z.number().int().nonnegative().nullable(), source: z.enum(['FARM', 'BUYER_PRICE', 'SETTING', 'NONE']),
  from: isoDate.optional(), to: isoDate.optional(), sample: z.number().int().nonnegative(), sampleUnit: z.string().max(80),
  reason: z.enum(['FEED_RATES_REQUIRED', 'INSUFFICIENT_DATA', 'OWNER_INPUT_REQUIRED']).optional(),
});
/** Validate API/cache data before using it for offline estimates. */
export const projectionDefaultsResponseSchema = z.object({
  asOf: isoDate,
  values: z.object(Object.fromEntries(Object.keys(valueShapes).map((key) => [key, farmFigureSchema]))),
  herdMaxPeakMl: farmFigureSchema,
}) as unknown as z.ZodType<ProjectionDefaultsResponse>;
